import { useBackend } from '@/hooks/useBackend';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useDialogStore } from '@/stores/dialogStore';
import { useTree } from '@/hooks/useTree';
import { useTreeStore } from '@/stores/treeStore';
import { log } from '@/lib/utils/logger';
import { TreeNode } from '@/types/tree';
import { TreeNodeFactory } from '@/lib/tree/treeNodeFactory';
import { performSubmitOperation, handleSubmitError, refreshAfterAction } from '@/lib/tree/submitEntityUtils';

/**
 * Custom hook for submitting entities with consistent logic across the app
 */
export const useSubmitEntity = () => {
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { showSubmitDialog, showConfirmDialog } = useDialogStore();
  const { setDataModified, getData, reloadNode, clearChildrenLoaded, invalidateChildrenCache, loadChildren } =
    useTree();
  const expandNode = useTreeStore((state) => state.expandNode);
  const setLoading = useTreeStore((state) => state.setLoading);
  const markSubmitting = (dataId: string, flag: boolean) => setLoading(`submit-${dataId}`, flag);

  /** Performs submit and refreshes the affected node to match context-menu behavior. */
  const performSubmit = async (node: TreeNode, userHasConfirmed: boolean = false): Promise<void> => {
    // Use shared utility for the actual backend submission
    await performSubmitOperation(node, backend, userHasConfirmed);
    await refreshAfterAction(
      node,
      backend,
      {
        reloadNode,
        clearChildrenLoaded,
        invalidateChildrenCache,
        loadChildren,
        expandNode,
      },
      { reloadNodeChildrenWhenSample: true }
    );
    setDataModified(node.dataId, false);

    showSnackbar(`${TreeNodeFactory.getNodeTypeLabel(node, getData)} ${node.label} submitted successfully`, 'success');
  };

  /**
   * Submits an entity with confirmation dialog and error handling
   * This is the main function to call from components
   */
  const submitEntity = async (node: TreeNode): Promise<boolean> => {
    try {
      markSubmitting(node.nodeId, true);

      // Show initial confirmation dialog
      const confirmed = await showSubmitDialog(TreeNodeFactory.getNodeTypeLabel(node), node.label);
      if (!confirmed) {
        return false;
      }

      try {
        await performSubmit(node, false); // First attempt with userHasConfirmed=false
        return true;
      } catch (err) {
        // Handle specific error cases that might require confirmation using shared utility
        const errorResult = await handleSubmitError(err, node, {
          showSnackbar,
          showConfirmDialog,
          getData,
        });

        if (errorResult.shouldRetry) {
          // Retry with userHasConfirmed=true after the user accepts the warning dialog.
          await performSubmit(node, true);
          return true;
        }

        // If shouldAbort is true, error handling already showed appropriate message
        return false;
      }
    } catch (error) {
      log.error('[useSubmitEntity] Unexpected error in submitEntity:', error);
      showSnackbar('Unexpected error occurred while submitting.', 'error');
      return false;
    } finally {
      markSubmitting(node.nodeId, false);
    }
  };

  return {
    submitEntity,
  };
};
